import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { driverSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { Driver } from './model'
import { User } from '../users/model'
import { DriversType } from '../drivers_types/model'
import { Country } from '../countries/model'
import * as usersActionCreators from '../users/actionCreators'
import * as driversTypesActionCreators from '../drivers_types/actionCreators'
import * as countriesActionCreators from '../countries/actionCreators'
import {fetchCountriesFulfilled} from "../countries/actionCreators";
import {fromJS} from "immutable";

// Fetch

const fetchDriversEpic = (action$, store) => {
    const tableIdentifier = 'drivers_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `drivers/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(driverSchema)
                        )

                        const drivers = normalizedEntitiesToRecordMap(normalizedData.entities.drivers, Driver, normalizedData.result)
                        const users = normalizedEntitiesToRecordMap(normalizedData.entities.users, User)
                        const driversTypes = normalizedEntitiesToRecordMap(normalizedData.entities.drivers_types, DriversType)
                        const countries = normalizedEntitiesToRecordMap(normalizedData.entities.countries, Country)

                        const observables = [
                            Observable.of(actionCreators.fetchDriversFulfilled(drivers)),
                            Observable.of(usersActionCreators.fetchUsersFulfilled(users)),
                            Observable.of(driversTypesActionCreators.fetchDriversTypesFulfilled(driversTypes)),
                            Observable.of(countriesActionCreators.fetchCountriesFulfilled(countries)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriversRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchDriverEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().drivers.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `drivers/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, driverSchema)

                        const drivers = normalizedEntitiesToRecordMap(normalizedData.entities.drivers, Driver)
                        const users = normalizedEntitiesToRecordMap(normalizedData.entities.users, User)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchDriversFulfilled(drivers)),
                            Observable.of(actionCreators.fetchDriverFulfilled()),
                            Observable.of(usersActionCreators.fetchUsersFulfilled(users))
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriversRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveDriverEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().drivers.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload.values)
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = `drivers`
            let method = 'POST'

            if (values.id) {
                path = `drivers/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(Observable.of(actionCreators.saveDriverDuplicityFound(ajaxResponse.response.duplicities)))
                        }

                        const normalizedData = normalize(ajaxResponse.response, driverSchema)

                        const drivers = normalizedEntitiesToRecordMap(normalizedData.entities.drivers, Driver)
                        const driver = drivers.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveDriverFulfilled(driver)))
                    })
                    .catch(error => Observable.of(actionCreators.saveDriverRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteDriverEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().drivers.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `drivers/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteDriverFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteDriverRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Restore

const restoreDriverEpic = (action$, store) =>
    action$
        .ofType(actionTypes.RESTORE)
        .filter(() => !store.getState().drivers.getIn(['restoring', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.RESTORE_STARTED,
                }),
                request({
                    path: `drivers/${action.payload}/restore`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, driverSchema)
                        const items = normalizedEntitiesToRecordMap(normalizedData.entities.drivers, Driver)
                        const item = items.valueSeq().first()
                        return Observable.concat(Observable.of(actionCreators.restoreDriverFulfilled(item)))
                    })
                    .catch(error => Observable.of(actionCreators.restoreDriverRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.RESTORE_CANCELLED))
            )
        )

// Export

const exportDriversEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `drivers/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

// History

const fetchDriverHistoryEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_HISTORY)
        .filter(() => !store.getState().drivers.getIn(['history', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_HISTORY_STARTED,
                }),
                request({
                    path: `drivers/${action.payload}/history`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchDriverHistoryFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchDriverHistoryRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_HISTORY_CANCELLED))
            )
        )

const exportDriverHistoryEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_HISTORY).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `drivers/${action.payload}/history/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_HISTORY_FULFILLED,
            })
        )
    })

// Documents

const fetchDriverDocumentsEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_DOCUMENTS)
        .filter(() => !store.getState().drivers.getIn(['documents', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_DOCUMENTS_STARTED,
                }),
                request({
                    path: `drivers/${action.payload.id}/documents${action.payload.page ? `?page=${action.payload.page}` : ''}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchDriverDocumentsFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchDriverDocumentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_DOCUMENTS_CANCELLED))
            )
        )

const exportDriverDocumentsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_DOCUMENTS).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `drivers/${action.payload}/documents/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_DOCUMENTS_FULFILLED,
            })
        )
    })

// Equipments

const fetchDriverEquipmentsEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_EQUIPMENTS)
        .filter(() => !store.getState().drivers.getIn(['equipments', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_EQUIPMENTS_STARTED,
                }),
                request({
                    path: `drivers/${action.payload.id}/equipment${action.payload.page ? `?page=${action.payload.page}` : ''}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchDriverEquipmentsFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchDriverEquipmentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_EQUIPMENTS_CANCELLED))
            )
        )

const exportDriverEquipmentsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_EQUIPMENTS).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `drivers/${action.payload}/equipment/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_EQUIPMENTS_FULFILLED,
            })
        )
    })

// Events

const fetchDriverEventsEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_EVENTS)
        .filter(() => !store.getState().drivers.getIn(['events', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_EVENTS_STARTED,
                }),
                request({
                    path: `drivers/${action.payload.id}/events${action.payload.page ? `?page=${action.payload.page}` : ''}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchDriverEventsFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchDriverEventsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_EVENTS_CANCELLED))
            )
        )

const exportDriverEventsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_EVENTS).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `drivers/${action.payload.id}/events/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_EVENTS_FULFILLED,
            })
        )
    })

// Timesheets

const fetchDriverTimesheetsEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_TIMESHEETS)
        .filter(() => !store.getState().drivers.getIn(['timesheets', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_TIMESHEETS_STARTED,
                }),
                request({
                    path: `drivers/${action.payload.id}/timesheet${action.payload.page ? `?page=${action.payload.page}` : ''}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchDriverTimesheetsFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchDriverTimesheetsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_TIMESHEETS_CANCELLED))
            )
        )

const exportDriverTimesheetsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_TIMESHEETS).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `drivers/${action.payload}/timesheet/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_TIMESHEETS_FULFILLED,
            })
        )
    })

// Allowances

const fetchDriverAllowancesEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ALLOWANCES)
        .filter(() => !store.getState().drivers.getIn(['allowances', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ALLOWANCES_STARTED,
                }),
                request({
                    path: `drivers/${action.payload.id}/allowances/config`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchDriverAllowancesFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchDriverAllowancesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ALLOWANCES_CANCELLED))
            )
        )

const saveDriverAllowancesEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE_ALLOWANCES)
        .filter(() => !store.getState().drivers.getIn(['allowances', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload)
            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_ALLOWANCES_STARTED,
                }),
                request({
                    path: `drivers/${values.data.driver_id}/allowances/config`,
                    method: 'POST',
                    body: values.data,
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.saveDriverAllowancesFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.saveDriverAllowancesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_ALLOWANCES_CANCELLED))
            )
        })

const deleteDriverAllowancesEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE_ALLOWANCES)
        .filter(() => !store.getState().drivers.getIn(['allowances', 'deleting']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload)
            return Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_ALLOWANCES_STARTED,
                }),
                request({
                    path: `drivers/${values.data.allowance_id}/allowances/config`,
                    method: 'DELETE',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.deleteDriverAllowanceFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.deleteDriverAllowanceRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_ALLOWANCES_CANCELLED))
            )
        })


// Fetch import headers

const fetchImportHeadersEpic = (action$, store) => {
    return action$
        .ofType(actionTypes.FETCH_IMPORT_HEADERS)
        .filter(() => !store.getState().drivers.getIn(['import', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_IMPORT_HEADERS_STARTED,
                }),
                request({
                    path: `drivers/import/get-headers`,
                    method: 'POST',
                    body: {
                        file: action.payload,
                    },
                })
                    .switchMap(ajaxResponse => {
                        const importHeaders = {
                            file_id: ajaxResponse.response.file_id,
                            headers: fromJS(ajaxResponse.response.headers),
                            headers_default: fromJS(ajaxResponse.response.headers_default),
                        }
                        return Observable.of(actionCreators.fetchDriversImportHeadersFulfilled(importHeaders))
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriversImportHeadersRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_IMPORT_HEADERS_CANCELLED))
            )
        )
}

// Fetch import items

const fetchImportItemsEpic = (action$, store) => {
    return action$
        .ofType(actionTypes.FETCH_IMPORT_ITEMS)
        .filter(() => !store.getState().drivers.getIn(['import', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_IMPORT_ITEMS_STARTED,
                }),
                request({
                    path: `drivers/import/get-items`,
                    method: 'POST',
                    body: {
                        fileId: action.payload.fileId,
                        columnTypes: action.payload.columnTypes,
                    },
                })
                    .switchMap(ajaxResponse => {
                        return Observable.of(actionCreators.fetchDriversImportItemsFulfilled(ajaxResponse.response.items))
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriversImportItemsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_IMPORT_ITEMS_CANCELLED))
            )
        )
}

// Import

const importDriversEpic = (action$, store) => {
    return action$
        .ofType(actionTypes.IMPORT_DRIVERS)
        .filter(() => !store.getState().drivers.getIn(['import', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.IMPORT_DRIVERS_STARTED,
                }),
                request({
                    path: `drivers/import/import`,
                    method: 'POST',
                    body: {
                        fileId: action.payload.fileId,
                        columnTypes: action.payload.columnTypes,
                        keys: action.payload.keys,
                    },
                })
                    .switchMap(ajaxResponse => {
                        return Observable.of(actionCreators.importDriversFulfilled())
                    })
                    .catch(error => Observable.of(actionCreators.importDriversRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.IMPORT_DRIVERS_CANCELLED))
            )
        )
}

export default [
    fetchDriversEpic,
    fetchDriverEpic,
    saveDriverEpic,
    deleteDriverEpic,
    restoreDriverEpic,
    exportDriversEpic,
    fetchDriverHistoryEpic,
    exportDriverHistoryEpic,
    fetchDriverDocumentsEpic,
    exportDriverDocumentsEpic,
    fetchDriverEventsEpic,
    exportDriverEventsEpic,
    fetchDriverEquipmentsEpic,
    exportDriverEquipmentsEpic,
    fetchDriverTimesheetsEpic,
    exportDriverTimesheetsEpic,
    fetchDriverAllowancesEpic,
    saveDriverAllowancesEpic,
    deleteDriverAllowancesEpic,
    fetchImportHeadersEpic,
    fetchImportItemsEpic,
    importDriversEpic,
]
